import { isTabKeepAliveEnabled } from '../config/tabKeepAlive';

let activeNavigationPath: string | null = null;
const syncPanePathStack: (string | null)[] = [];
const attributionRefCounts = new Map<string, number>();

export const setKeepAliveActiveNavigationPath = (path: string | null): void => {
  activeNavigationPath = path;
};

export const getKeepAliveActiveNavigationPath = (): string | null => activeNavigationPath;

export const runWithPaneHttpContext = <T>(panePath: string | null, fn: () => T): T => {
  syncPanePathStack.push(panePath);
  try {
    return fn();
  } finally {
    syncPanePathStack.pop();
  }
};

export const getExecutingPaneHttpPath = (): string | null =>
  syncPanePathStack.length > 0
    ? syncPanePathStack[syncPanePathStack.length - 1]
    : null;

export const acquirePaneHttpAttribution = (panePath: string): (() => void) => {
  attributionRefCounts.set(panePath, (attributionRefCounts.get(panePath) ?? 0) + 1);
  return () => {
    releasePaneHttpAttribution(panePath);
  };
};

export const releasePaneHttpAttribution = (panePath: string): void => {
  const count = attributionRefCounts.get(panePath) ?? 0;
  if (count <= 1) {
    attributionRefCounts.delete(panePath);
    return;
  }
  attributionRefCounts.set(panePath, count - 1);
};

const resolveAttributedPanePath = (): string | undefined => {
  if (attributionRefCounts.size !== 1) {
    return undefined;
  }
  return attributionRefCounts.keys().next().value;
};

export const resolveKeepAliveRequestPanePath = (
  explicitPanePath?: string,
): string | undefined => {
  if (explicitPanePath) return explicitPanePath;
  const executing = getExecutingPaneHttpPath();
  if (executing) return executing;
  return resolveAttributedPanePath();
};

export const keepAliveRequestConfig = (
  panePath: string,
): { keepAlivePanePath: string } => ({
  keepAlivePanePath: panePath,
});

/**
 * Bloqueia requests HTTP disparados de panes keep-alive inativos.
 * Requests sem panePath (layout, auth, etc.) continuam permitidos.
 */
export const shouldAllowKeepAliveHttpRequest = (panePath?: string): boolean => {
  if (!isTabKeepAliveEnabled()) return true;
  if (!panePath) return true;
  if (!activeNavigationPath) return true;
  return panePath === activeNavigationPath;
};

/** Limpa estado interno — apenas para testes. */
export const resetKeepAliveNetworkGateForTests = (): void => {
  activeNavigationPath = null;
  syncPanePathStack.length = 0;
  attributionRefCounts.clear();
};
